import { useCallback, useEffect, useState } from 'react';
import { Sample } from '../../@types/samples/types';

const STORAGE_KEY = 'scal-sample-history';

function storageKey(projectUid: string) {
  return `${STORAGE_KEY}:${projectUid}`;
}

function readHistory(projectUid: string): Sample[] {
  try {
    const raw = localStorage.getItem(storageKey(projectUid));
    const parsed = raw ? (JSON.parse(raw) as Sample[]) : [];
    const currentProjectSamples = parsed.filter(sample => sample.project_uid === projectUid);
    if (currentProjectSamples.length !== parsed.length) {
      writeHistory(projectUid, currentProjectSamples);
    }
    return currentProjectSamples;
  } catch {
    return [];
  }
}

function writeHistory(projectUid: string, samples: Sample[]) {
  try {
    localStorage.setItem(storageKey(projectUid), JSON.stringify(samples));
  } catch {
    // localStorage quota exceeded or unavailable — silently ignore
  }
}

export function useSampleHistory(projectUid: string | null) {
  const [history, setHistory] = useState<Sample[]>(() =>
    projectUid ? readHistory(projectUid) : [],
  );

  useEffect(() => {
    setHistory(projectUid ? readHistory(projectUid) : []);
  }, [projectUid]);

  const addToHistory = useCallback(
    (sample: Sample) => {
      if (!projectUid) return;
      if (sample.project_uid !== projectUid) return;
      setHistory(prev => {
        const alreadyInHistory = prev.some(s => s.uid === sample.uid);
        if (alreadyInHistory) return prev;

        const next = [...prev, sample];
        writeHistory(projectUid, next);
        return next;
      });
    },
    [projectUid],
  );

  const removeFromHistory = useCallback(
    (sampleUid: string) => {
      if (!projectUid) return;
      setHistory(prev => {
        const next = prev.filter(sample => sample.uid !== sampleUid);
        writeHistory(projectUid, next);
        return next;
      });
    },
    [projectUid],
  );

  return { history, addToHistory, removeFromHistory };
}
